import { functionalUpdate } from '@tanstack/react-table';
import {
  type ReactElement,
  type ReactNode,
  type PropsWithChildren,
  Children,
} from 'react';
import { isElement } from 'react-is';

import { getPaginationRowModel } from './pagination-row-model.js';
import type {
  DataTablePaginationProps,
  DataTablePaginationState,
} from './pagination-types.js';
import { useControllableState } from '../../../../core/hooks/useControllableState.js';
import { useDataTableConfigElement } from '../../../hooks/useDataTableConfig.js';
import {
  DATA_TABLE_DEFAULT_PAGE_SIZE_OPTIONS,
  DATA_TABLE_DEFAULT_PAGINATION_PAGE_INDEX,
  DATA_TABLE_DEFAULT_PAGE_SIZE_OPTION,
} from '../../constants.js';
import type {
  Table,
  TableFeature,
  TableOptions,
  Updater,
} from '../../hooks/useTable/types.js';
import type { DataTableRowData } from '../../row-data-types.js';

/**
 * DataTable slot definition.
 * @public
 */
export function DataTablePagination(props: DataTablePaginationProps) {
  return null;
}

/**
 * Helper function that will check if a passed component is of type `DataTablePagination`
 * @internal
 */
export function _isDataTablePagination(
  child: ReactNode,
): child is ReactElement<DataTablePaginationProps> {
  return isElement(child) && child.type === DataTablePagination;
}

/**
 * Returns the default page size based on `defaultPageSize` being
 * passed as a prop, or the first option of `pageSizeOptions` array
 * if `defaultPageSize` is passed.
 * @internal
 * @defaultValue 100
 */
function getDefaultPageSize(
  pageSizeOptions?: number[],
  defaultPageSize?: number,
): number {
  if (typeof defaultPageSize === 'number') {
    return defaultPageSize;
  }

  if (Array.isArray(pageSizeOptions) && pageSizeOptions[0]) {
    return pageSizeOptions[0];
  }

  return DATA_TABLE_DEFAULT_PAGE_SIZE_OPTION;
}

/**
 * @internal
 */
export const DataTablePaginationFeature: TableFeature = {
  createTable<TData extends DataTableRowData>(table: Table<TData>): void {
    table.setPagination = (updater, trigger) => {
      const safeUpdater: Updater<DataTablePaginationState> = (old) => {
        const newState: DataTablePaginationState = functionalUpdate(
          updater,
          old,
        );

        return newState;
      };

      return table.options.onPaginationChange?.(safeUpdater, trigger);
    };

    table.setPageIndex = (updater, trigger) => {
      table.setPagination((old) => {
        let pageIndex = functionalUpdate(updater, old.pageIndex);

        const maxPageIndex =
          typeof table.options.pageCount === 'undefined' ||
          table.options.pageCount === -1
            ? Number.MAX_SAFE_INTEGER
            : table.options.pageCount - 1;

        pageIndex = Math.max(0, Math.min(pageIndex, maxPageIndex));

        return {
          ...old,
          pageIndex,
        };
      }, trigger);
    };

    table.resetPageIndex = (defaultState) => {
      table.setPageIndex(
        defaultState
          ? DATA_TABLE_DEFAULT_PAGINATION_PAGE_INDEX
          : table.initialState?.pagination?.pageIndex ??
              DATA_TABLE_DEFAULT_PAGINATION_PAGE_INDEX,
        'internal',
      );
    };

    table.getPrePaginationRowModel = () => table.getRowOrderRowModel();
  },
};

/**
 * Configuration hook for pagination of the DataTable.
 * @internal
 */
export function usePagination<TData>(
  props: PropsWithChildren,
  options: TableOptions<TData>,
): void {
  const configPagination = useDataTableConfigElement('pagination');

  const paginationElement = Children.toArray(props.children).find(
    _isDataTablePagination,
  );

  // Init the paginationOptions to make sure they are a valid boolean.
  options.enablePagination = false;

  const paginationSizeOptions = paginationElement?.props.pageSizeOptions ?? [
    ...DATA_TABLE_DEFAULT_PAGE_SIZE_OPTIONS,
  ];

  const defaultPaginationSizeOption = getDefaultPageSize(
    paginationElement?.props.pageSizeOptions,
    paginationElement?.props.defaultPageSize ??
      configPagination?.defaultPageSize,
  );

  const [pageSize, setPageSize] = useControllableState({
    value: paginationElement?.props.pageSize,
    defaultValue: defaultPaginationSizeOption,
    onChange: paginationElement?.props.onPageSizeChange,
  });

  const defaultPaginationIndexOption =
    paginationElement?.props.defaultPageIndex ??
    configPagination?.defaultPageIndex ??
    DATA_TABLE_DEFAULT_PAGINATION_PAGE_INDEX;

  const [pageIndex, setPageIndex] = useControllableState({
    value: paginationElement?.props.pageIndex,
    defaultValue: defaultPaginationIndexOption,
  });

  if (!paginationElement) {
    return;
  }

  options.enablePagination = true;
  options.availablePaginationOptions = paginationSizeOptions;
  options.controlledPageIndex = paginationElement.props.pageIndex !== undefined;

  if (paginationElement.props.autoResetPageIndex === false) {
    options.autoResetPageIndex = paginationElement.props.autoResetPageIndex;
  }

  // Server side pagination.
  if (
    paginationElement.props.enableNextPage !== undefined &&
    paginationElement.props.enablePreviousPage !== undefined
  ) {
    options.manualPagination = true;
    // Ensure pageCount is considered unknown to react table.
    options.pageCount = -1;
  } else {
    options.manualPagination = false;
    options.getPaginationRowModel = getPaginationRowModel();
  }
  options.state = {
    ...options.state,
    pagination: {
      pageSize,
      pageIndex,
      enablePreviousPage: paginationElement.props.enablePreviousPage,
      enableNextPage: paginationElement.props.enableNextPage,
      totalRowsCount: paginationElement.props.totalRowsCount,
    },
  };

  options.onPaginationChange = (
    updater: Updater<DataTablePaginationState>,
    trigger: 'user' | 'internal' = 'user',
  ) => {
    const nextState: DataTablePaginationState = functionalUpdate(
      updater,
      options.state?.pagination ?? {
        pageSize,
        pageIndex,
      },
    );

    if (nextState?.pageSize !== pageSize && nextState?.pageSize !== undefined) {
      setPageSize(nextState.pageSize);
      // Reset page index once page size changes
      setPageIndex(DATA_TABLE_DEFAULT_PAGINATION_PAGE_INDEX);
      paginationElement?.props.onPageIndexChange?.(
        DATA_TABLE_DEFAULT_PAGINATION_PAGE_INDEX,
        'internal',
      );
    } else if (
      nextState?.pageIndex !== pageIndex &&
      nextState?.pageIndex !== undefined
    ) {
      setPageIndex(nextState.pageIndex);
      paginationElement?.props.onPageIndexChange?.(
        nextState.pageIndex,
        trigger,
      );
    }
  };
}
